// src/prototype/data/rationaleSummary.ts
// ============================================================
// Pure derivations for the Scoring Rationale tab. Nothing here is authored copy: every figure
// is read from the scoring view model (getScores) and ordered, never invented.
//
// WHAT "STRONGEST" AND "WEAKEST" MEAN. The composite is NOT computed from measure scores on the
// frontend (it comes from the authored category inputs through the dimension weights), so these
// lists never claim a measure's "contribution to the composite". They rank how a measure
// stands: first by its weight tier (High, then Medium, then Low), then by how favourable its
// score is. An unweighted measure ranks below Low.
//
// DIRECTION. The scoring code applies no inversion (recomputeScores is a plain weighted mean),
// but the measure rationales show that some measures are scored as a BURDEN: a 5 on "Challenges"
// reads "Very high obstacles". For ranking only, a burden measure is reverse-scored so a low
// burden counts as favourable; the score shown to the reader is always the real one.
// ============================================================

import type { DimensionVM, MeasureVM } from './adapters';

/** Weight tiers in rank order. An index, not a score: High is first. */
const WEIGHT_ORDER = ['High', 'Medium', 'Low'] as const;

/**
 * Measures verified, from the highest-scored rationale text in the generated and stage
 * fixtures, to be scored as a burden ("Very high obstacles", "Very high effort", ...). Keyed
 * by catalog measure id. The cue completes "higher =". Measures NOT listed are shown as
 * higher-is-better; several are ambiguous in the data (measures 26, 30, 34, 38, 45 and 47) and are deliberately left unmarked rather than guessed.
 */
export const BURDEN_MEASURE_CUES: Readonly<Record<number, string>> = {
  4: 'more obstacles', // measure 4: 5 = "Very high obstacles"
  27: 'more effort', // measure 27: 5 = "Very high effort"
  29: 'greater need', // measure 29: 5 = "Very high need for robust ETL"
  35: 'greater need', // measure 35: 5 = "Very high need"
  40: 'more complexity', // measure 40: 5 = "Very high complexity"
  41: 'more requirements', // measure 41: 5 = "Very high consent needs"
  43: 'more expertise required', // measure 43: 5 = "Very high expertise required"
  51: 'more complexity', // measure 51: 5 = "Very high implementation complexity"
};

export const measureNumericId = (key: string): number | null => {
  const m = /^m?(\d+)$/.exec(key.trim());
  return m ? Number(m[1]) : null;
};

/** "more obstacles" for a burden measure, else null. */
export function burdenCue(m: Pick<MeasureVM, 'id'>): string | null {
  const id = measureNumericId(m.id);
  return id !== null ? BURDEN_MEASURE_CUES[id] ?? null : null;
}

export const scaleMax = (m: Pick<MeasureVM, 'scale'>) => (m.scale === '1to5' ? 5 : 100);

/** Score for ORDERING ONLY: reverse-scored when the measure is a burden. */
export function favourability(m: MeasureVM): number {
  const raw = m.raw ?? 0;
  if (burdenCue(m) === null) return raw;
  return m.scale === '1to5' ? 6 - raw : 100 - raw;
}

export interface RankedMeasure {
  measure: MeasureVM;
  dim: DimensionVM;
}

const tierIndex = (m: MeasureVM) => {
  const i = m.weight ? WEIGHT_ORDER.indexOf(m.weight) : -1;
  return i === -1 ? WEIGHT_ORDER.length : i;
};

/** Evaluated, scored measures from the given dimensions, strongest first. */
export function rankMeasures(dims: readonly DimensionVM[]): RankedMeasure[] {
  return dims
    .flatMap((dim) => dim.measures.map((measure) => ({ measure, dim })))
    .filter(({ measure }) => measure.evaluated && measure.raw !== null)
    .sort(
      (a, b) =>
        tierIndex(a.measure) - tierIndex(b.measure) ||
        favourability(b.measure) - favourability(a.measure),
    );
}

/** Top and bottom `n`; the weakest list never repeats a measure already listed as strongest. */
export function strongestAndWeakest(dims: readonly DimensionVM[], n = 3) {
  const ranked = rankMeasures(dims);
  const strongest = ranked.slice(0, n);
  const taken = new Set(strongest.map((r) => r.measure.id));
  const weakest = [...ranked]
    .sort(
      (a, b) =>
        tierIndex(a.measure) - tierIndex(b.measure) ||
        favourability(a.measure) - favourability(b.measure),
    )
    .filter((r) => !taken.has(r.measure.id))
    .slice(0, n);
  return { strongest, weakest };
}

/** The strongest measure inside one dimension, or null when it has none scored. */
export function strongestInDimension(dim: DimensionVM): MeasureVM | null {
  return rankMeasures([dim])[0]?.measure ?? null;
}

/** '4.0/5' or '82/100'. */
export function formatMeasureScore(m: MeasureVM): string {
  const raw = m.raw ?? 0;
  return m.scale === '1to5' ? `${raw.toFixed(1)}/5` : `${Math.round(raw)}/100`;
}

/** "a, b and c". */
export function joinList(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** "raised business value by 9.5", "lowered implementation ease by 1.8", or "left ... unchanged". */
export function adjustmentPhrase(label: string, delta: number): string {
  if (delta === 0) return `left ${label} unchanged`;
  return `${delta > 0 ? 'raised' : 'lowered'} ${label} by ${Math.abs(delta)}`;
}

export interface EvidenceTrace {
  src: string;
  /** Catalog measure ids (keys as stored) whose evidence carries this trace line. */
  measureIds: string[];
}

/** Every distinct raw evidence source line, with the measures that carry it. Aggregated, not repeated. */
export function collectEvidenceTraces(dims: readonly DimensionVM[]): EvidenceTrace[] {
  const bySrc = new Map<string, string[]>();
  for (const d of dims) {
    for (const m of d.measures) {
      for (const e of m.ev) {
        const ids = bySrc.get(e.src) ?? [];
        if (!ids.includes(m.id)) ids.push(m.id);
        bySrc.set(e.src, ids);
      }
    }
  }
  return Array.from(bySrc, ([src, measureIds]) => ({ src, measureIds }));
}

export interface CompositeSegment {
  id: DimensionVM['id'];
  /** The dimension score the composite used (0 to 100). */
  score: number;
  /** Its weight, 0 to 1. */
  weight: number;
  /** score x weight, unrounded: this dimension's points of the composite. */
  points: number;
}

/**
 * How the composite is built, from the SAME dimension scores and weights it was computed from
 * (rollupComposite: the weighted sum of the four dimension scores). Undefined when the
 * composite did not come from a dimension-weight rollup. Segments follow `order`.
 */
export function compositeBreakdown(
  dims: readonly DimensionVM[],
  weights: Readonly<Record<DimensionVM['id'], number>> | undefined,
  order: readonly DimensionVM['id'][],
): CompositeSegment[] | undefined {
  if (!weights) return undefined;
  const segments: CompositeSegment[] = [];
  for (const id of order) {
    const d = dims.find((x) => x.id === id);
    if (!d) return undefined;
    segments.push({ id, score: d.score, weight: weights[id], points: d.score * weights[id] });
  }
  return segments;
}
